import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from 'api/config';
import { useChatAnalytics } from 'app/analytics';
import { logError } from 'lib/sentry/logError';
import { useMutationHTTPRequest } from 'shared_DEPRECATED/hooks';

import { ChatResponseType } from 'features/chat/config/types/types';
import { useChatContext } from 'features/chat/context/chat';
import { useChatInterlocutorContext } from 'features/chat/context/chatInterlocutor';

export const useToggleChatReadStatusMutation = () => {
  const { chatId } = useChatInterlocutorContext();
  const { request } = useMutationHTTPRequest();
  const queryClient = useQueryClient();

  const { sendChatMarkedAsUnreadAnalyticsEvent } = useChatAnalytics();

  const { isChatMarkedAsUnread } = useChatContext();

  return useMutation(
    async () =>
      request({
        url: `/api/chats/${chatId}/return-to-mark/edit`,
        body: { returnToMark: !isChatMarkedAsUnread },
      }),
    {
      onMutate: async () => {
        await queryClient.cancelQueries({
          queryKey: [queryKeys.chats, chatId],
        });

        const previousValue = queryClient.getQueryData<
          ChatResponseType | undefined
        >([queryKeys.chats, chatId]);

        if (previousValue) {
          queryClient.setQueryData<ChatResponseType | undefined>(
            [queryKeys.chats, chatId],
            (oldData) =>
              oldData
                ? { ...oldData, markedAsReturnTo: !isChatMarkedAsUnread }
                : oldData
          );
        }

        return { previousValue };
      },
      onError: (error, _, context) => {
        queryClient.setQueryData(
          [queryKeys.chats, chatId],
          context?.previousValue
        );

        logError(error);
      },
      onSuccess: () => {
        if (!isChatMarkedAsUnread) {
          sendChatMarkedAsUnreadAnalyticsEvent();
        }
      },
    }
  );
};
